import { createAction } from '@reduxjs/toolkit'
import isUndefined from 'lodash/isUndefined'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { prepareActiveDrawableBeforeSubmit } from '../utils/prepareActiveDrawableBeforeSubmit'
import { fetchOpeningGroupPending, setActiveDrawableGroup, updateOpeningGroupsSuccess } from '../../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../../api/api-helper'
import { updateJoistLines, updateOpeningGroup } from '../../../api/takeoff-api'
import { ActiveDrawable, Opening_location, OpeningGroup } from '../../../models/activeDrawable'
import { GeneralDrawableSettings } from '../../../models/activeDrawableSettings'
import { Region } from '../../../models/region'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { updateDrawableSelections } from '../../../shared/services/opening-form-services'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectActiveMode } from '../../slices/common'
import { formError } from '../../slices/forms'
import { ActiveGeometryGroup } from '../../slices/geometry'
import { VIEW_MODE } from '../../types'

export const submitMaterialFormAndRefresh = createAction<{ formData: OpeningGroup['settings'] }>(
    'submitMaterialFormAndRefresh'
)

export function* handleMaterialFormSubmitAndRefresh({ payload }: ReturnType<typeof submitMaterialFormAndRefresh>) {
    try {
        const activeMode = yield select(selectActiveMode)

        const manager: PaperManager | null =
            activeMode === VIEW_MODE.Markup3D ? yield call(managers.get3DManager) : yield call(managers.get2DManager)

        if (!manager) return

        const results: {
            activeDrawableGroup: ActiveGeometryGroup
            prepareDrawables: (input: {
                drawables: ActiveDrawable[]
                newSettings: GeneralDrawableSettings
                scaleFactor: number
                dpi: number | null
                pdfScale: number
                xCalibrationFactor: number
                yCalibrationFactor: number
                regions: Region[]
                is3D?: boolean
            }) => ActiveDrawable[]
            openings: any[] | null
            settings: any
            configuration: any
            remarks: any
            scaleFactor: number
            dpi: number
            pdfScale: number
            xCalibrationFactor: number
            yCalibrationFactor: number
            regions: Region[]
        } = yield call(prepareActiveDrawableBeforeSubmit, { formData: payload.formData })

        // Something went wrong with preparing data for submit bail
        if (!results) return

        const {
            activeDrawableGroup,
            prepareDrawables,
            openings,
            settings,
            configuration,
            remarks,
            scaleFactor,
            dpi,
            xCalibrationFactor,
            yCalibrationFactor,
            pdfScale,
            regions,
        } = results

        yield put(fetchOpeningGroupPending())

        if (!openings) return

        const req = {
            openings: isUndefined(prepareDrawables)
                ? updateDrawableSelections(openings, {
                      settings,
                      configuration,
                      remarks,
                  })
                : prepareDrawables({
                      drawables: updateDrawableSelections(openings, {
                          settings,
                          configuration,
                          remarks,
                      }),
                      newSettings: settings,
                      scaleFactor,
                      dpi,
                      xCalibrationFactor,
                      yCalibrationFactor,
                      regions,
                      pdfScale,
                  }),
            settings: {
                ...settings,
                // For ledger with EWP materials, set ewp_width and height to numeric values for backend
                ...(activeDrawableGroup.type === DRAWABLE_TYPES.LEDGER &&
                    ['GL', 'LSL', 'LVL', 'PSL'].includes(settings.material) && {
                        ewp_width: settings.width_other || settings.ewp_width,
                        height: settings.height_other || settings.height,
                    }),
            },
            type: activeDrawableGroup.type,
            configuration,
            remarks,
            isSave: true,
        }

        const res = yield call(updateOpeningGroup, activeDrawableGroup.project_id, req, String(activeDrawableGroup.id))

        // Note: below uses as unknown as X type hacking to
        // streamline the code for conforming to API types,
        // this is not strictly correct but will work as
        // the discrepancies are largely a result of complexity
        // of typing (unused properties in document chunk on opening locations)
        // or due to eccentricities in the types due to the fact that
        // the drawable slice was untyped.
        const formattedResponse: UpdateOpeningGroupApiResponse = {
            newGroup: res,
            originalGroup: {
                id: activeDrawableGroup.id,
                project_id: activeDrawableGroup.project_id,
                color: null,
                ...req,
                // Restore original dropdown values for ewp_width and height to prevent form display issues
                settings: {
                    ...req.settings,
                    ...(activeDrawableGroup.type === DRAWABLE_TYPES.LEDGER &&
                        ['GL', 'LSL', 'LVL', 'PSL'].includes(settings.material) &&
                        settings.width_other && {
                            ewp_width: 'other',
                        }),
                    ...(activeDrawableGroup.type === DRAWABLE_TYPES.LEDGER &&
                        ['GL', 'LSL', 'LVL', 'PSL'].includes(settings.material) &&
                        settings.height_other && {
                            height: settings.height,
                        }),
                },
                openings: req.openings.map((opening) => {
                    return {
                        ...opening,
                        settings: opening.settings as unknown as GeneralDrawableSettings,
                        opening_locations: opening.opening_locations.map((loc) => {
                            return {
                                ...loc,
                                id: loc.id ?? -1,
                                opening_id: loc.opening_id ?? -1,
                                region_id: loc.region_id ?? null,
                                src: '',
                                cutouts: loc.cutouts ?? [],
                                document_chunk: loc.document_chunk as unknown as Opening_location['document_chunk'],
                                three_d_identifier: loc.three_d_identifier ?? null,
                            }
                        }),
                        outputs: {},
                    }
                }),
            },
        }

        yield put(updateOpeningGroupsSuccess(formattedResponse))

        yield put(
            setActiveDrawableGroup(
                formattedResponse.newGroup,
                req.openings.map((opening) => opening.id)
            )
        )

        // Only the selected openings were submitted; regenerate joist lines for those alone so the
        // same material on other pages keeps its lines.
        yield put(
            yield call(
                updateJoistLines,
                formattedResponse.newGroup.project_id,
                formattedResponse.newGroup.id,
                req.openings.map((opening) => opening.id)
            )
        )

        if (activeMode === VIEW_MODE.Markup2D) {
            const [workspaceTool]: [Workspace, Select] = yield call(manager.getTools, [Workspace.NAME])

            const arrowItem: paper.Item = yield call(workspaceTool.getArrowElement)

            if (arrowItem) {
                yield call(workspaceTool.removeItemWithPaperId, arrowItem.id)
            }
        }
    } catch (error) {
        yield put(formError((error as any).message))
        console.error(error)
    }
}

export function* watchForMaterialFormSubmitAndRefresh() {
    yield takeLatest(submitMaterialFormAndRefresh.type, handleMaterialFormSubmitAndRefresh)
}
